(() => {
  if (window.EmslandrostProductGuide) {
    window.EmslandrostProductGuide.init(document);
    return;
  }

  const ROOT = '[data-em-product-guide]';
  const CONTENT = '.em-product-guide__technical-content';
  const instances = new Map();

  // Ignore CSS and scripts: an app placeholder can be nonempty but invisible.
  function hasRows(content) {
    if (!content) return false;
    const copy = content.cloneNode(true);
    copy.querySelectorAll('script, style, template, noscript').forEach((node) => node.remove());
    const text = copy.textContent ?? [...copy.childNodes].map(node => node.textContent || '').join('');
    return Boolean(text.trim());
  }

  class ProductGuide {
    constructor(root) {
      this.root = root;
      this.content = root.querySelector(CONTENT);
      this.card = root.querySelector('.em-product-guide__technical');
      this.status = root.querySelector('[data-em-technical-status]');
      this.retry = root.querySelector('[data-em-technical-retry]');
      this.link = root.querySelector('[data-em-technical-link]');
      this.descriptionCard = root.querySelector('[data-em-description-card]');
      this.descriptionCopy = root.querySelector('.em-product-guide__description-copy');
      this.descriptionLink = root.querySelector('[data-em-description-link]');
      this.variantId = root.dataset.variantId || '';
      this.revision = 0;
      this.loading = false;
      this.failed = false;
      this.timer = null;
      this.controller = null;
      this.onClick = () => this.ensureData();
      this.onRetry = () => this.load();
      this.link?.addEventListener('click', this.onClick);
      this.retry?.addEventListener('click', this.onRetry);
      this.observer = new MutationObserver(() => {
        if (this.content.dataset.emTechnicalMode === 'app' && !this.loading && !this.failed) {
          this.schedule(400);
        }
      });
      this.observer.observe(this.content, { childList: true, subtree: true, characterData: true });
      if (this.descriptionCopy) {
        this.descriptionObserver = new MutationObserver(() => this.updateDescription());
        this.descriptionObserver.observe(this.descriptionCopy, { childList: true, subtree: true, characterData: true });
      }
      this.updateDescription();
      this.normalize();
      this.schedule(700);
    }

    normalize() {
      // Legacy Guru tables use both real table rows and div-based rows.
      // Keep all values, while applying the theme's legible table styling.
      this.content.querySelectorAll('table').forEach(table => table.classList.add('em-technical-data__table'));
      const manual = document.querySelector('.em-product-download-app[data-document-type="installation"] a[href]');
      if (!manual) return;
      this.content.querySelectorAll('th, td, dt, span, div').forEach(label => {
        if (label.children.length || !/^(Einbauanleitung|Einbauanleitung vorhanden)$/.test(label.textContent.trim())) return;
        const value = label.nextElementSibling;
        if (value && /^(?:✗\s*|✘\s*)?(Nein|Ja)$/i.test(value.textContent.trim()) && value.textContent.trim() !== 'Ja – PDF unter Downloads') {
          value.textContent = 'Ja – PDF unter Downloads';
        }
      });
    }

    renderedVariant() {
      if (this.content.dataset.emTechnicalMode === 'app') {
        return this.content.querySelector('[data-mg-variant-id]')?.dataset.mgVariantId
          || this.content.dataset.variantId;
      }
      return this.content.dataset.variantId;
    }

    setMessage(message, allowRetry = false) {
      this.status.textContent = message;
      this.status.hidden = !message;
      this.retry.hidden = !allowRetry;
    }

    updateNavigation() {
      this.link.hidden = this.card.hidden;
      const navigation = this.root.querySelector('.em-product-guide__jump-links');
      if (navigation) {
        navigation.hidden = [...navigation.querySelectorAll('a')].filter((link) => !link.hidden).length < 2;
      }
      if (this.root.dataset.designMode !== 'true') {
        this.root.hidden = !((this.descriptionCard && !this.descriptionCard.hidden)
          || !this.card.hidden || this.root.querySelector('.em-product-guide__topics, .em-product-guide__faq'));
      }
    }

    updateDescription() {
      if (this.descriptionCard) {
        const present = hasRows(this.descriptionCopy)
          || Boolean(this.descriptionCopy?.querySelector('img[src], video, iframe[src], table td'));
        this.descriptionCard.hidden = !present;
        if (this.descriptionLink) this.descriptionLink.hidden = !present;
      }
      this.updateNavigation();
    }

    schedule(delay) {
      window.clearTimeout(this.timer);
      this.timer = window.setTimeout(() => this.ensureData(), delay);
    }

    ensureData() {
      if (!this.root.isConnected || !this.variantId || this.loading) return;
      if (this.content.dataset.emTechnicalMode === 'empty'
          && this.renderedVariant() === this.variantId && !this.failed) return;
      if (this.content.dataset.variantId !== this.variantId || this.renderedVariant() !== this.variantId || !hasRows(this.content) || this.failed) {
        this.load();
      } else {
        this.normalize();
        this.content.hidden = false;
        this.content.setAttribute('aria-busy', 'false');
        this.setMessage('');
      }
    }

    selectVariant(variantId) {
      const nextId = variantId ? String(variantId) : '';
      if (nextId === this.variantId) {
        this.ensureData();
        return;
      }
      // Invalidate before the debounce so a slow response cannot show old data.
      this.revision += 1;
      this.controller?.abort();
      this.controller = null;
      this.loading = false;
      window.clearTimeout(this.timer);
      this.variantId = nextId;
      this.failed = false;
      this.content.hidden = true;
      this.content.setAttribute('aria-busy', nextId ? 'true' : 'false');
      this.root.hidden = false;
      this.card.hidden = false;
      this.updateNavigation();
      this.setMessage(nextId ? 'Technische Daten werden geladen …' : 'Bitte wählen Sie eine gültige Ausführung.');
      if (nextId) this.schedule(120);
    }

    async load() {
      if (!this.variantId || !this.root.isConnected) return;
      window.clearTimeout(this.timer);
      this.controller?.abort();
      const controller = new AbortController();
      this.controller = controller;
      const revision = ++this.revision;
      const variantId = this.variantId;
      this.loading = true;
      this.failed = false;
      this.content.hidden = true;
      this.content.setAttribute('aria-busy', 'true');
      this.card.hidden = false;
      this.root.hidden = false;
      this.updateNavigation();
      this.setMessage('Technische Daten werden geladen …');
      const timeout = window.setTimeout(() => controller.abort(), 12000);

      try {
        const url = new URL(window.location.href);
        url.hash = '';
        url.searchParams.delete('sections');
        url.searchParams.set('section_id', this.root.dataset.sectionId);
        url.searchParams.set('variant', variantId);
        const response = await fetch(url, { credentials: 'same-origin', signal: controller.signal });
        if (!response.ok) throw new Error('Section request: ' + response.status);
        const html = await response.text();
        if (revision !== this.revision || !this.root.isConnected) return;

        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const freshRoot = [...parsed.querySelectorAll(ROOT)].find((node) =>
          node.dataset.sectionId === this.root.dataset.sectionId
          && node.dataset.productId === this.root.dataset.productId);
        const freshContent = freshRoot?.querySelector(CONTENT);
        if (!freshContent || freshContent.dataset.variantId !== variantId) {
          throw new Error('Technical data response does not match the selected variant.');
        }
        let mode = freshContent.dataset.emTechnicalMode;
        if (!hasRows(freshContent)) {
          const fallback = freshRoot.querySelector('[data-em-technical-fallback]');
          const fallbackContent = fallback?.content;
          if (fallbackContent && hasRows(fallbackContent)) {
            freshContent.replaceChildren(...Array.from(fallbackContent.childNodes));
            mode = 'variant';
          } else {
            freshContent.replaceChildren();
            mode = 'empty';
          }
        }

        // Keep the existing product form and never execute copied app scripts.
        freshContent.querySelectorAll('script').forEach((script) => script.remove());
        this.content.replaceChildren(...Array.from(freshContent.childNodes).map((node) => document.importNode(node, true)));
        this.content.dataset.emTechnicalMode = mode;
        this.content.dataset.variantId = variantId;
        this.content.hidden = false;
        this.normalize();
        this.root.dataset.variantId = variantId;
        this.root.hidden = freshRoot.hidden;
        this.card.hidden = mode === 'empty';
        this.setMessage('');
        this.updateNavigation();
      } catch (error) {
        if (revision !== this.revision || !this.root.isConnected) return;
        this.failed = true;
        this.setMessage('Die technischen Daten konnten nicht geladen werden. Bitte versuchen Sie es erneut.', true);
        // Old-variant specifications stay hidden if a request fails.
      } finally {
        window.clearTimeout(timeout);
        if (revision === this.revision) {
          this.loading = false;
          this.controller = null;
          this.content.setAttribute('aria-busy', 'false');
        }
      }
    }

    destroy() {
      this.revision += 1;
      window.clearTimeout(this.timer);
      this.controller?.abort();
      this.observer.disconnect();
      this.descriptionObserver?.disconnect();
      this.link?.removeEventListener('click', this.onClick);
      this.retry?.removeEventListener('click', this.onRetry);
    }
  }

  function init(scope = document) {
    instances.forEach((guide, root) => {
      if (!root.isConnected) {
        guide.destroy();
        instances.delete(root);
      }
    });
    const roots = [...scope.querySelectorAll(ROOT)];
    if (scope.matches?.(ROOT)) roots.unshift(scope);
    roots.forEach((root) => {
      if (!instances.has(root) && root.querySelector(CONTENT)) instances.set(root, new ProductGuide(root));
    });
  }

  document.addEventListener('on:variant:change', (event) => {
    const picker = event.target.closest?.('variant-picker');
    // Quick-add selectors must not change the main product's specifications.
    if (picker?.dataset.updateUrl === 'false' || picker?.closest('quick-add-drawer, quick-add, .quick-add')) return;
    const productId = event.detail?.product?.id;
    if (!productId) return;
    instances.forEach((guide, root) => {
      if (root.dataset.productId === String(productId)) guide.selectVariant(event.detail?.variant?.id);
    });
  });

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    instances.forEach((guide, root) => {
      if (event.target.contains(root)) {
        guide.destroy();
        instances.delete(root);
      }
    });
  });
  window.EmslandrostProductGuide = { init };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }
})();
